/**
 * Clock link + cancel X shown next to a "Workflow paused" chat message.
 * Clock opens the schedule page; X cancels the wait and resumes the thread.
 */

import React from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ScheduleIcon from "@mui/icons-material/Schedule";

export interface WorkflowTimerScheduleActionsProps {
  scheduleHref?: string | null;
  showScheduleLink?: boolean;
  canCancel?: boolean;
  canceling?: boolean;
  compact?: boolean;
  onCancel?: () => void;
}

const iconButtonSx = (compact?: boolean) => ({
  width: compact ? 30 : 34,
  height: compact ? 30 : 34,
  p: 0,
  border: "1px solid #8F6ED5",
  borderRadius: "6px",
  color: "#6D55B8",
  backgroundColor: "#FFFFFF",
  flexShrink: 0,
  "&:hover": {
    borderColor: "#7054C8",
    backgroundColor: "rgba(160,132,232,0.08)",
  },
  "&.Mui-disabled": {
    borderColor: "#E5E7EB",
    color: "#9CA3AF",
  },
});

const WorkflowTimerScheduleActions: React.FC<WorkflowTimerScheduleActionsProps> = ({
  scheduleHref,
  showScheduleLink = true,
  canCancel = false,
  canceling = false,
  compact = false,
  onCancel,
}) => {
  if (!showScheduleLink && !canCancel) return null;
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
      {showScheduleLink && scheduleHref ? (
        <Tooltip title="Open schedule" arrow>
          <IconButton
            component="a"
            href={scheduleHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open schedule"
            size="small"
            onClick={(event) => event.stopPropagation()}
            sx={iconButtonSx(compact)}
          >
            <ScheduleIcon sx={{ fontSize: compact ? 16 : 18 }} />
          </IconButton>
        </Tooltip>
      ) : null}
      {canCancel && onCancel ? (
        <Tooltip title="Cancel schedule" arrow>
          <IconButton
            aria-label="Cancel schedule"
            size="small"
            disabled={canceling}
            onClick={(event) => {
              event.stopPropagation();
              onCancel();
            }}
            sx={iconButtonSx(compact)}
          >
            <CloseIcon sx={{ fontSize: compact ? 16 : 18 }} />
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  );
};

export default WorkflowTimerScheduleActions;
